import React, { useState, useRef, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { MessageCircle, Send, X, Loader2, HelpCircle } from 'lucide-react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
}

interface SupportTicket {
  id: string;
  customer: {
    name: string;
    email: string;
    phone: string;
    description: string;
  };
  status: 'Open' | 'In Progress' | 'Resolved';
  priority: 'High' | 'Medium' | 'Low';
  createdAt: string;
  lastUpdated: string;
  isRead: boolean;
  messages: Message[];
  expiresAt: string;
  internalNotes: {
    id: string;
    content: string;
    createdAt: string;
    createdBy: string;
  }[];
}

interface TicketFormValues {
  name: string;
  email: string;
  phone: string;
  description: string;
}

const ticketFormSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string()
    .matches(/^\d{10}$/, 'Please enter a valid 10-digit phone number')
    .required('Phone number is required'),
  description: Yup.string()
    .required('Description is required')
    .min(10, 'Please provide more details'),
});

const supportMessages = {
  confirmation: "Thank you for submitting your support ticket. Our team will review it and respond to you shortly.",
  error: "I apologize, but there was an error submitting your ticket. Please try again.",
};

export function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setShowTicketForm(true);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBotResponse = (content: string) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'bot',
        content,
        timestamp: new Date()
      }]);
      setIsTyping(false);
    }, 1000);
  };

  const handleSubmitTicket = async (values: TicketFormValues) => {
    try {
      // Calculate expiry date (30 days from now)
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30);

      // Create new support ticket
      const newTicket: SupportTicket = {
        id: Date.now().toString(),
        customer: {
          name: values.name,
          email: values.email,
          phone: values.phone,
          description: values.description
        },
        status: 'Open',
        priority: 'Medium',
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        isRead: false,
        messages: messages,
        expiresAt: expiryDate.toISOString(),
        internalNotes: []
      };

      // Get existing tickets or initialize empty array
      const existingTickets = JSON.parse(localStorage.getItem('supportTickets') || '[]');
      
      // Add new ticket
      const updatedTickets = [...existingTickets, newTicket];
      
      // Save to localStorage
      localStorage.setItem('supportTickets', JSON.stringify(updatedTickets));

      // Add user's ticket details as a message
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'user',
        content: `Submitted ticket: ${values.description}`,
        timestamp: new Date()
      }]);

      handleBotResponse(supportMessages.confirmation);
      setShowTicketForm(false);
    } catch (error) {
      console.error('Error submitting ticket:', error);
      handleBotResponse(supportMessages.error);
    }
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <button
          className="fixed bottom-24 right-4 z-[70] rounded-full bg-blue-600 p-4 text-white shadow-lg transition-colors hover:bg-blue-700 md:bottom-4"
          aria-label="Submit Support Ticket"
        >
          <HelpCircle className="w-6 h-6" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="fixed bottom-40 right-4 z-[70] flex w-[calc(100vw-2rem)] max-w-[380px] flex-col rounded-lg bg-white shadow-xl md:bottom-20"
          sideOffset={5}
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-blue-600 text-white rounded-t-lg">
            <div>
              <h3 className="font-semibold">Submit Support Ticket</h3>
              <p className="text-sm text-blue-100">We'll respond within 24 hours</p>
            </div>
            <Popover.Close className="rounded-full p-2 hover:bg-blue-700 transition-colors">
              <X className="w-5 h-5" />
            </Popover.Close>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 space-y-4 overflow-y-auto max-h-[400px]">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.type === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                  <span className="text-xs opacity-70 mt-1 block">
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex items-center text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Support bot is typing...
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Support Ticket Form */}
          {showTicketForm && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <Formik
                initialValues={{
                  name: '',
                  email: '',
                  phone: '',
                  description: ''
                }}
                validationSchema={ticketFormSchema}
                onSubmit={handleSubmitTicket}
              >
                {({ errors, touched, isSubmitting }) => (
                  <Form className="space-y-4">
                    <div>
                      <Field
                        name="name"
                        placeholder="Your Name"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {errors.name && touched.name && (
                        <div className="text-red-500 text-xs mt-1">{errors.name}</div>
                      )}
                    </div>

                    <div>
                      <Field
                        name="email"
                        type="email"
                        placeholder="Email Address"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {errors.email && touched.email && (
                        <div className="text-red-500 text-xs mt-1">{errors.email}</div>
                      )}
                    </div>

                    <div>
                      <Field
                        name="phone"
                        type="tel"
                        placeholder="Phone Number (10 digits)"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {errors.phone && touched.phone && (
                        <div className="text-red-500 text-xs mt-1">{errors.phone}</div>
                      )}
                    </div>

                    <div>
                      <Field
                        as="textarea"
                        name="description"
                        placeholder="How can we help you?"
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={4}
                      />
                      {errors.description && touched.description && (
                        <div className="text-red-500 text-xs mt-1">{errors.description}</div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Support Ticket'}
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}